/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import ResultsModal from './ResultsModal.js';
import ThemeDisplay from './ThemeDisplay.js';
import MovieQuiz from './MovieQuiz.js';
import LoadingScreen from './LoadingScreen.js';
import { trackDailyQuizStart, trackQuizCompletion, trackHintUsage, trackCorrectAnswer, trackIncorrectAnswer, trackDarkModeToggle, trackDatePickerUsage, trackFeedbackSubmission, trackShareResults } from './analytics.js';
import { DateTime } from 'luxon';
import levenshtein from "fast-levenshtein";

function ClassicGame({ user, userId, isAuthenticated, isDarkMode, toggleTheme, stats, fetchStats, authLoading, selectedDate, setSelectedDate, completedQuizDates, fetchHistoricalQuizzes }) {
    const [quiz, setQuiz] = useState(null);
    const [questionStatuses, setQuestionStatuses] = useState({
        easy: { status: 'unanswered', hints: { year: false, genre: false, director: false, mainActor: false }, mistakes: 0, graded: false, emoji: ['🟩'], placeholder: "Enter your guess here" },
        medium: { status: 'unanswered', hints: { year: false, genre: false, director: false, mainActor: false }, mistakes: 0, graded: false, emoji: ['🟦'], placeholder: "Enter your guess here" },
        hard: { status: 'unanswered', hints: { year: false, genre: false, director: false, mainActor: false }, mistakes: 0, graded: false, emoji: ['🟪'], placeholder: "Enter your guess here" },
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [results, setResults] = useState('');
    const [error, setError] = useState(null);
    const [mistakesMade, setMistakesMade] = useState(0);
    const [answers, setAnswers] = useState(0);
    const [hintsUsed, setHintsUsed] = useState(0);
    const [loading, setLoading] = useState(false);
    const [resultsShared, setResultsShared] = useState(false);
    const [populationStats, setPopulationStats] = useState(null);
    const [gameSlug] = useState('emoji-quiz');
    const [feedback, setFeedback] = useState(false);
    const [toast, setToast] = useState(null);

    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || '/api';

    const areAllAnswersDone = useCallback(() => {
        return Object.values(questionStatuses).every(status => status.status !== 'unanswered');
    }, [questionStatuses]);

    const updateProgress = useCallback(async (progress, date, quizStatus = 'in_progress') => {
        if (!quiz || !userId) return;
        try {
            await axios.post(`${apiEndpoint}/progress`, {
                userId,
                progress,
                quizId: quiz?.id,
                date: date,
                quizStatus,
                gameId: quiz?.game_id
            });
            setQuestionStatuses(progress);
        } catch (error) {
            console.error('Progress update error:', error.message);
        }
    }, [apiEndpoint, quiz, userId]);

    // Unified handle completion function to be used during init AND gameplay
    const handleCompletion = useCallback(async (currentQuiz, currentStatuses) => {
        if (resultsShared) return;
        setResultsShared(true);

        const resultsString = `${currentStatuses["easy"].emoji.join('')}\n${currentStatuses["medium"].emoji.join('')}\n${currentStatuses["hard"].emoji.join('')}`;
        setResults(resultsString);

        // Submit results to server
        const mistakes = currentStatuses["easy"].mistakes + currentStatuses["medium"].mistakes + currentStatuses["hard"].mistakes;
        setMistakesMade(mistakes);

        const hints = (Object.values(currentStatuses["easy"].hints).filter(Boolean).length) +
            (Object.values(currentStatuses["medium"].hints).filter(Boolean).length) +
            (Object.values(currentStatuses["hard"].hints).filter(Boolean).length);
        setHintsUsed(hints);

        try {
            const response = await axios.post(`${apiEndpoint}/results`, {
                userId,
                gameId: currentQuiz?.game_id,
                quizId: currentQuiz?.id,
                correctCount: Object.values(currentStatuses).filter(s => s.status === 'correct').length,
                hintsUsed: hints,
                progress: currentStatuses,
                date: selectedDate
            });
            setPopulationStats(response.data.stats);
            fetchStats();
            fetchHistoricalQuizzes();
        } catch (error) {
            console.error('Error submitting results:', error.message);
        }

        await updateProgress(currentStatuses, selectedDate, 'completed');
        setIsModalOpen(true);
    }, [apiEndpoint, resultsShared, selectedDate, userId, fetchStats, fetchHistoricalQuizzes, updateProgress]);

    useEffect(() => {
        if (quiz && areAllAnswersDone() && !resultsShared && !loading) {
            handleCompletion(quiz, questionStatuses);
        }
    }, [questionStatuses, resultsShared, quiz, loading, areAllAnswersDone, handleCompletion]);

    useEffect(() => {
        if (!selectedDate || !userId || authLoading) return;

        const controller = new AbortController();
        setLoading(true);

        async function initializeGame() {
            try {
                // 1. Prepare clean slate
                setQuiz(null);
                setResultsShared(false);
                setIsModalOpen(false);
                setAnswers(0);
                setMistakesMade(0);
                setHintsUsed(0);
                setQuestionStatuses({
                    easy: { status: 'unanswered', hints: { year: false, genre: false, director: false, mainActor: false }, mistakes: 0, graded: false, emoji: ['🟩'], placeholder: "Enter your guess here" },
                    medium: { status: 'unanswered', hints: { year: false, genre: false, director: false, mainActor: false }, mistakes: 0, graded: false, emoji: ['🟦'], placeholder: "Enter your guess here" },
                    hard: { status: 'unanswered', hints: { year: false, genre: false, director: false, mainActor: false }, mistakes: 0, graded: false, emoji: ['🟪'], placeholder: "Enter your guess here" },
                });

                // 2. Fetch Quiz and Progress in parallel
                const [quizRes, progressRes] = await Promise.all([
                    fetch(`${apiEndpoint}/quiz?game_slug=${gameSlug}&date=${selectedDate}`, { signal: controller.signal }),
                    axios.get(`${apiEndpoint}/progress?userId=${userId}&quizId=${selectedDate}&gameId=${gameSlug}`, { signal: controller.signal })
                ]);

                if (!quizRes.ok) throw new Error(`Quiz fetch failed: ${quizRes.status}`);
                const quizData = await quizRes.json();

                // 3. Hydrate all states together
                let finalStatuses = {
                    easy: { status: 'unanswered', hints: { year: false, genre: false, director: false, mainActor: false }, mistakes: 0, graded: false, emoji: ['🟩'], placeholder: "Enter your guess here" },
                    medium: { status: 'unanswered', hints: { year: false, genre: false, director: false, mainActor: false }, mistakes: 0, graded: false, emoji: ['🟦'], placeholder: "Enter your guess here" },
                    hard: { status: 'unanswered', hints: { year: false, genre: false, director: false, mainActor: false }, mistakes: 0, graded: false, emoji: ['🟪'], placeholder: "Enter your guess here" },
                };
                let finalAnswers = 0;
                let finalMistakes = 0;
                let finalHints = 0;
                let finalIsModalOpen = false;
                let finalResultsShared = false;
                let finalPopulationStats = null;

                if (progressRes.data && (progressRes.data.progress || progressRes.data.quiz_status === 'completed')) {
                    let savedProgress = progressRes.data.progress || {};

                    if (progressRes.data.quiz_status === 'completed' && Object.keys(savedProgress).length === 0) {
                        savedProgress = {
                            easy: { status: 'correct', hints: { year: false, genre: false, director: false, mainActor: false }, mistakes: 0, graded: true, emoji: ['🟩', '✅'], placeholder: "Enter your guess here" },
                            medium: { status: 'correct', hints: { year: false, genre: false, director: false, mainActor: false }, mistakes: 0, graded: true, emoji: ['🟦', '✅'], placeholder: "Enter your guess here" },
                            hard: { status: 'correct', hints: { year: false, genre: false, director: false, mainActor: false }, mistakes: 0, graded: true, emoji: ['🟪', '✅'], placeholder: "Enter your guess here" }
                        };
                    }

                    const isValidProgress = ['easy', 'medium', 'hard'].every(level =>
                        savedProgress[level] && Array.isArray(savedProgress[level].emoji)
                    );

                    if (isValidProgress) {
                        finalStatuses = savedProgress;

                        ['easy', 'medium', 'hard'].forEach(level => {
                            const p = savedProgress[level];
                            if (p.status === 'correct') finalAnswers++;
                            finalMistakes += (p.mistakes || 0);
                            if (p.hints) {
                                finalHints += Object.values(p.hints).filter(Boolean).length;
                            }
                        });

                        const isDone = Object.values(savedProgress).every(status => status.status !== 'unanswered');
                        if (isDone) {
                            const resultsString = `${savedProgress["easy"].emoji.join('')}\n${savedProgress["medium"].emoji.join('')}\n${savedProgress["hard"].emoji.join('')}`;
                            setResults(resultsString);
                            finalResultsShared = true;
                            finalIsModalOpen = true;

                            try {
                                const resStats = await axios.get(`${apiEndpoint}/results?userId=${userId}&quizId=${quizData.id}`);
                                if (resStats.data && resStats.data.stats) {
                                    finalPopulationStats = resStats.data.stats;
                                }
                            } catch (e) {
                                // Silent fallback
                            }
                        }
                    }
                }

                setQuiz(quizData);
                setQuestionStatuses(finalStatuses);
                setAnswers(finalAnswers);
                setMistakesMade(finalMistakes);
                setHintsUsed(finalHints);
                setResultsShared(finalResultsShared);
                setIsModalOpen(finalIsModalOpen);
                if (finalPopulationStats) setPopulationStats(finalPopulationStats);

                trackDailyQuizStart(quizData.date);
                setError(null);
            } catch (err) {
                if (!axios.isCancel(err) && err.name !== 'AbortError' && err.message !== 'signal is aborted without reason') {
                    console.error('Initialization error:', err);
                    setError(err);
                }
            } finally {
                setLoading(false);
            }
        }

        initializeGame();
        return () => controller.abort();
    }, [selectedDate, userId, authLoading, gameSlug, apiEndpoint]);

    const updateFeedback = async (likes, dislikes, date) => {
        try {
            await axios.post(`${apiEndpoint}/feedback`, {
                quizId: quiz?.id,
                likes,
                dislikes
            });
            setFeedback(true);
            trackFeedbackSubmission(likes, dislikes);
        } catch (error) {
            console.error(error.message);
        }
    };

    const checkLevenshteinAnswer = (guess, level, threshold) => {
        const userInputLower = guess?.toLowerCase();
        const movieDetails = quiz.data ? quiz.data[level] : quiz[level];
        const correctTitles = movieDetails?.answers || null;
        const correctTitlesLower = correctTitles?.map((title) => title.toLowerCase()) || [];
        if (correctTitlesLower?.includes(userInputLower)) return true;
        for (const title of correctTitlesLower) {
            const distance = levenshtein.get(userInputLower, title);
            if (distance <= threshold) return true;
        }
        return false;
    };

    const checkAnswer = async (guess, level) => {
        const updatedStatuses = { ...questionStatuses };
        if (updatedStatuses[level].status === 'revealed') {
            updatedStatuses[level].status = 'incorrect';
            updatedStatuses[level].emoji = [...updatedStatuses[level].emoji, '❌']
            await new Promise(resolve => setTimeout(resolve, 200));
        } else {
            const levenshteinAnswer = checkLevenshteinAnswer(guess, level, 3);
            if (levenshteinAnswer && updatedStatuses[level].status === 'unanswered') {
                setAnswers(answers + 1);
                updatedStatuses[level].status = 'correct';
                updatedStatuses[level].emoji = [...updatedStatuses[level].emoji, '✅']
                await new Promise(resolve => setTimeout(resolve, 200));
                trackCorrectAnswer(level, updatedStatuses[level].mistakes + 1);
            } else {
                if (checkLevenshteinAnswer(guess, level, 6)) {
                    updatedStatuses[level].placeholder = "So close! Try again!"
                } else {
                    updatedStatuses[level].placeholder = "Try again"
                }
                updatedStatuses[level].mistakes = updatedStatuses[level].mistakes + 1
                updatedStatuses[level].emoji = [...updatedStatuses[level].emoji, '⚪️']
                await new Promise(resolve => setTimeout(resolve, 200));
                trackIncorrectAnswer(level, updatedStatuses[level].mistakes);
                if (updatedStatuses[level].mistakes >= 4) {
                    updatedStatuses[level].status = 'incorrect';
                    updatedStatuses[level].emoji = [...updatedStatuses[level].emoji, '❌']
                    await new Promise(resolve => setTimeout(resolve, 200));
                }
            }
        }
        setQuestionStatuses(updatedStatuses);
        await updateProgress(updatedStatuses, selectedDate);
        fetchHistoricalQuizzes();
    };

    const revealHint = (level, hintType) => {
        const updatedStatuses = { ...questionStatuses };
        if (updatedStatuses[level] && updatedStatuses[level].hints) {
            updatedStatuses[level].hints[hintType] = true;
            updatedStatuses[level].emoji = [...updatedStatuses[level].emoji, '💡']
            setQuestionStatuses(updatedStatuses);
            trackHintUsage(level, hintType);
            updateProgress(updatedStatuses, selectedDate);
        }
    };

    const shareResults = `mojie classic\n"${quiz?.theme}"\n${quiz?.date}\n\n${results}\n\nhttps://mojiequiz.com?game=classic&date=${quiz?.date}`
    const copyResultsToClipboard = useCallback(() => {
        navigator.clipboard.writeText(shareResults)
            .catch(err => console.error('Failed to copy results:', err));
    }, [shareResults]);

    const closeModal = useCallback(() => setIsModalOpen(false), []);

    if (loading || authLoading || !quiz) return <LoadingScreen loading={true} />;

    return (
        <div className="game-wrapper classic-game">
            <ThemeDisplay theme={quiz.theme} isDarkMode={isDarkMode} date={selectedDate} />
            <div className="movie-levels">
                {['easy', 'medium', 'hard'].map(level => (
                    <MovieQuiz
                        key={level}
                        level={level}
                        movie={quiz.data ? quiz.data[level] : quiz[level]}
                        onGuess={checkAnswer}
                        onRevealHint={revealHint}
                        questionStatus={questionStatuses[level]}
                        isDarkMode={isDarkMode}
                    />
                ))}
            </div>

            {toast && <div className="toast">{toast}</div>}

            {areAllAnswersDone() && (
                <div className="view-results-container">
                    <button
                        className="mojie-btn primary"
                        onClick={() => setIsModalOpen(true)}
                    >
                        View Results →
                    </button>
                </div>
            )}
            <ResultsModal
                isOpen={isModalOpen}
                onClose={closeModal}
                results={results}
                onCopy={copyResultsToClipboard}
                theme={quiz ? quiz.theme : null}
                themeExplanation={quiz ? quiz.theme_explanation : null}
                date={quiz ? quiz.date : selectedDate}
                isDarkMode={isDarkMode}
                mistakes={mistakesMade}
                answers={answers}
                hints={hintsUsed}
                updateFeedback={updateFeedback}
                populationStats={populationStats}
                stats={stats}
            />
        </div>
    );
}

export default ClassicGame;
